import React, { useCallback, useEffect, useState } from 'react';
import { Box, Typography, Button, CircularProgress } from '@mui/material';
import { Print } from '@mui/icons-material';
import { useRouter } from '../contexts/RouterContext';
import { useAuth } from '../contexts/AuthContext';
import {
  professionalServiceValueService,
  ServiceValuesMatrix,
  ServiceValuesMatrixService,
} from '../services/professionalServiceValueService';
import { printTemplateService, renderTemplateHtml, PrintTemplate } from '../services/printTemplateService';
import { PrintTemplateBar, usePrintTemplateChoice } from './print/PrintTemplateChoice';

/**
 * Impressão dos valores por serviço (pedido do Hilton, 19/09/2026): a matriz da equipe —
 * serviço × profissional, com valor, repasse e tempo de sessão. É o papel que se leva para
 * conferir repasse com o terapeuta e para negociar tabela.
 *
 * <p>Um bloco por SERVIÇO, na mesma folha enquanto couber (ao contrário da Produtividade, que dá
 * uma folha por profissional: lá a folha é entregue a cada um, aqui o documento é de quem
 * administra). Cabeçalho e rodapé saem do template padrão da entidade e se repetem por página
 * via thead/tfoot (RN-534e).</p>
 *
 * <p>Recebe os ids pela URL e busca os dados: a matriz não caberia em parâmetro de URL.</p>
 */
const fmtMoney = (n?: number | null) =>
  n == null ? '—' : `R$ ${n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const ServiceValuesPrint: React.FC = () => {
  const { getParam } = useRouter();
  const { user } = useAuth();

  const professionalTeam = getParam('professionalTeam') || '';
  const professionalType = getParam('professionalType') || '';
  const serviceType = getParam('serviceType') || '';

  const [matrix, setMatrix] = useState<ServiceValuesMatrix | null>(null);
  const [templates, setTemplates] = useState<PrintTemplate[]>([]);
  const templateChoice = usePrintTemplateChoice(templates, 'service-values');
  const template = templateChoice.template;
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');

  const carregar = useCallback(async () => {
    if (!professionalTeam || !professionalType) {
      setErro('Abra a impressão a partir da tela de Valores por serviço.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setErro('');
    try {
      setMatrix(await professionalServiceValueService.getMatrix({
        professionalTeam,
        professionalType,
        serviceType: serviceType || undefined,
      }));
    } catch {
      setErro('Não foi possível carregar os valores.');
      setMatrix(null);
    } finally {
      setLoading(false);
    }
  }, [professionalTeam, professionalType, serviceType]);

  useEffect(() => {
    document.title = 'Valores por serviço';
    // Template PADRÃO da entidade; sem nenhum cadastrado a folha sai sem cabeçalho, e não quebra.
    if (user?.entityId) {
      printTemplateService
        .getByEntity(user.entityId)
        .then(setTemplates)
        .catch(() => setTemplates([]));
    }
  }, [user?.entityId]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const cabecalhoClinica = () => template && (
    <div className="print-frame-header">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '6mm' }}>
        <div style={{ flexShrink: 0 }}>
          {template?.file ? (
            <img src={template.file} alt="logo" style={{ maxHeight: '30mm', maxWidth: '70mm', objectFit: 'contain' }} />
          ) : null}
        </div>
        <div
          style={{ textAlign: 'right', fontSize: '10pt', lineHeight: 1.5 }}
          dangerouslySetInnerHTML={{ __html: renderTemplateHtml(template?.header) }}
        />
      </div>
      <hr style={{ border: 'none', borderTop: '1px solid #999', margin: '6mm 0' }} />
    </div>
  );

  const rodapeClinica = () => template && (
    <div
      style={{ textAlign: 'center', fontSize: '9pt', paddingTop: '6mm' }}
      dangerouslySetInnerHTML={{ __html: renderTemplateHtml(template?.footer) }}
    />
  );

  const celulaCabecalho: React.CSSProperties = {
    padding: '2mm 3mm', border: '1px solid #ccc', background: '#f1f3f5',
    fontWeight: 600, textAlign: 'left',
  };
  const celula: React.CSSProperties = {
    padding: '2mm 3mm', border: '1px solid #ccc', overflowWrap: 'anywhere',
  };

  const secaoServico = (servico: ServiceValuesMatrixService) => {
    const semValor = servico.professionals.filter(
      (p) => p.serviceValue == null && p.professionalPaymentVal == null,
    ).length;
    return (
      <Box key={servico.serviceTypeId} sx={{ mb: '6mm' }} className="servico-bloco">
        <Typography sx={{ fontSize: '11pt', fontWeight: 700, color: '#212529' }}>
          {servico.description}
        </Typography>
        <Typography sx={{ fontSize: '9.5pt', color: '#495057', mb: '2mm' }}>
          {servico.origin === 'operator'
            ? [servico.healthPlanName, servico.procedureCode ? `código ${servico.procedureCode}` : null]
              .filter(Boolean).join(' · ')
            : 'Serviço próprio'}
          {' · '}
          {servico.professionals.length} profissional{servico.professionals.length === 1 ? '' : 'is'}
          {semValor > 0 ? ` · ${semValor} sem valor cadastrado` : ''}
        </Typography>

        <table style={{
          width: '100%', tableLayout: 'fixed', borderCollapse: 'collapse', fontSize: '10pt',
        }}>
          <thead>
            <tr>
              <th style={celulaCabecalho}>Profissional</th>
              <th style={{ ...celulaCabecalho, width: '28mm', textAlign: 'right' }}>Valor</th>
              <th style={{ ...celulaCabecalho, width: '28mm', textAlign: 'right' }}>Repasse</th>
              <th style={{ ...celulaCabecalho, width: '22mm', textAlign: 'center' }}>Sessão</th>
              <th style={{ ...celulaCabecalho, width: '24mm', textAlign: 'center' }}>Rec. indep.</th>
            </tr>
          </thead>
          <tbody>
            {servico.professionals.map((p) => (
              <tr key={p.professionalRegistration}>
                <td style={celula}>{p.professionalName || '—'}</td>
                <td style={{ ...celula, textAlign: 'right' }}>{fmtMoney(p.serviceValue)}</td>
                <td style={{ ...celula, textAlign: 'right' }}>{fmtMoney(p.professionalPaymentVal)}</td>
                <td style={{ ...celula, textAlign: 'center' }}>
                  {p.sessionTimeMinutes != null ? `${p.sessionTimeMinutes} min` : '—'}
                </td>
                <td style={{ ...celula, textAlign: 'center' }}>
                  {p.professionalPaymentInc ? 'Sim' : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Box>
    );
  };

  const temDados = !!matrix && matrix.services.length > 0;

  return (
    <Box sx={{
      minHeight: '100vh',
      backgroundColor: '#f5f5f5',
      padding: { xs: '0.75rem', sm: '2rem' },
      '@media print': { padding: 0, backgroundColor: '#fff' },
    }}>
      <style>{`
        .print-frame { width: 100%; border-collapse: collapse; }
        .print-frame > thead { display: table-header-group; }
        .print-frame > tfoot { display: table-footer-group; }

        @media print {
          @page { size: A4 portrait; margin: 12mm; }
          .print-frame { min-height: 262mm; }
          .print-frame > thead > tr > td,
          .print-frame > tbody > tr > td { vertical-align: top; padding: 0; }
          .print-frame > tfoot > tr > td { vertical-align: bottom; padding: 0; }
          .print-frame-header img { max-height: 18mm !important; }
          /* O bloco do serviço não se parte ao meio quando cabe inteiro na próxima folha */
          .servico-bloco { break-inside: avoid; }
          tr { break-inside: avoid; }
        }
      `}</style>

      <Box className="no-print" sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
        <PrintTemplateBar choice={templateChoice} />
        <Button
          variant="contained" startIcon={<Print />} onClick={() => window.print()}
          disabled={loading || !temDados}
        >
          Imprimir
        </Button>
      </Box>

      <Box>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress size={28} />
          </Box>
        ) : erro ? (
          <Typography sx={{ p: 3, fontSize: 14, color: '#dc3545' }}>{erro}</Typography>
        ) : !temDados ? (
          <Typography className="no-print" sx={{ p: 3, fontSize: 14, color: '#6c757d' }}>
            Nenhum serviço com profissionais elegíveis nesta equipe.
          </Typography>
        ) : (
          /* Uma moldura só: o cabeçalho da clínica se repete em toda página pelo thead. */
          <table className="print-frame">
            <thead><tr><td>{cabecalhoClinica()}</td></tr></thead>
            <tfoot><tr><td>{rodapeClinica()}</td></tr></tfoot>
            <tbody><tr><td>
              <Box sx={{ mb: '4mm' }}>
                <Typography sx={{ fontSize: '13pt', fontWeight: 700, color: '#212529' }}>
                  Valores por serviço
                </Typography>
                <Typography sx={{ fontSize: '10pt', color: '#495057' }}>
                  {matrix!.teamName}
                  {matrix!.professionalTypeName ? ` · ${matrix!.professionalTypeName}` : ''}
                  {' · '}
                  {matrix!.services.length} serviço{matrix!.services.length === 1 ? '' : 's'}
                </Typography>
              </Box>

              {matrix!.services.map(secaoServico)}

              <Typography sx={{ fontSize: '9pt', color: '#6c757d' }}>
                Emitido em {new Date().toLocaleDateString('pt-BR')}
              </Typography>
            </td></tr></tbody>
          </table>
        )}
      </Box>
    </Box>
  );
};

export default ServiceValuesPrint;
